import type { NetworkVerdict, ReputationSignals } from '@fraudeai/schema';

/**
 * Кому принадлежат адреса.
 *
 * Отдельная секция, потому что это единственный слой, который видит
 * полнотуннельный VPN. Пробы сравнивают адреса между транспортами и ловят
 * туннель, пропускающий только TCP. Туннель, заворачивающий весь трафик,
 * расхождения не даёт — и виден только по тому, что адрес зарегистрирован
 * на дата-центр, а не на оператора связи.
 */

const KIND_LABELS: Record<string, string> = {
  vpn: 'сеть VPN-провайдера',
  hosting: 'дата-центр',
  mobile: 'мобильный оператор',
  isp: 'оператор связи',
  education: 'учебная сеть',
  unknown: 'не определена',
};

/** Типы сетей, из которых приходят обычные пользователи. */
const RESIDENTIAL = ['isp', 'mobile', 'education'];

function describe(verdict: NetworkVerdict): string {
  const parts = [verdict.ip];
  if (verdict.asName) parts.push(`AS${verdict.asn} ${verdict.asName}`);
  if (verdict.country) parts.push(verdict.country);
  return parts.join(' · ');
}

export function ReputationPanel({ reputation }: { reputation: ReputationSignals }) {
  if (!reputation.available) {
    return (
      <section>
        <h3>Принадлежность адреса</h3>
        <p className="hint">
          Базы принадлежности адресов не загружены, поэтому проверка на дата-центр, VPN и
          узлы Tor не выполнялась. Именно она ловит туннели, пропускающие через себя весь
          трафик: расхождений между транспортами такой VPN не даёт.
        </p>
      </section>
    );
  }

  const client = reputation.client;
  const rows: { label: string; value: string; bad: boolean }[] = [];

  if (client) {
    const suspicious = !RESIDENTIAL.includes(client.kind);
    rows.push({
      label: 'Тип сети',
      value: KIND_LABELS[client.kind] ?? client.kind,
      bad: suspicious,
    });
    if (client.asName) {
      rows.push({ label: 'Владелец сети', value: `AS${client.asn} ${client.asName}`, bad: false });
    }
    if (client.country) {
      rows.push({ label: 'Страна регистрации', value: client.country, bad: false });
    }
    rows.push({
      label: 'Выходной узел Tor',
      value: client.tor ? 'да' : 'нет',
      bad: Boolean(client.tor),
    });
    if (client.blocklists?.length) {
      rows.push({ label: 'Чёрные списки', value: client.blocklists.join(', '), bad: true });
    }
    if (client.openPorts?.length) {
      rows.push({
        label: 'Открытые порты прокси',
        value: client.openPorts.map((p) => `${p.port} (${p.service})`).join(', '),
        bad: true,
      });
    }
  }

  const others: [string, NetworkVerdict | undefined][] = [
    ['Сеть WebRTC', reputation.stun],
    ['Сеть HTTP/3', reputation.http3],
  ];

  return (
    <section>
      <h3>Принадлежность адреса</h3>
      <p className="hint">
        Домашний пользователь приходит из сети оператора связи. Адрес, зарегистрированный
        на хостинг-провайдера или на поставщика VPN, — единственный признак, по которому
        виден туннель, пропускающий через себя весь трафик.
      </p>

      <div className="scroll-x">
        <table>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="name" style={{ width: '34%' }}>
                  {row.label}
                </td>
                <td style={row.bad ? { color: 'var(--fraudulent)' } : undefined}>{row.value}</td>
              </tr>
            ))}
            {others.map(([label, verdict]) =>
              verdict ? (
                <tr key={label}>
                  <td className="name">{label}</td>
                  <td
                    style={
                      client && verdict.asn && client.asn && verdict.asn !== client.asn
                        ? { color: 'var(--fraudulent)' }
                        : undefined
                    }
                  >
                    {describe(verdict)}
                  </td>
                </tr>
              ) : null,
            )}
            {(reputation.dns ?? []).map((resolver, index) => (
              <tr key={`dns-${index}`}>
                <td className="name">Сеть резолвера DNS</td>
                <td
                  style={
                    client?.country && resolver.country && resolver.country !== client.country
                      ? { color: 'var(--fraudulent)' }
                      : undefined
                  }
                >
                  {describe(resolver)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
